import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Loader2, Sparkles, CheckCircle2, X as XIcon } from "lucide-react";
import Modal from "./Modal";
import useVoiceCapture from "../lib/useVoiceCapture";

/**
 * BlurTextarea — commit-on-blur textarea with an inline voice mic icon.
 *
 * Bugfix history (2026-04-30): the previous implementation reset `draft`
 * whenever the parent's `value` prop changed, silently wiping in-progress
 * typing. Fix: only resync from props when the field is unfocused.
 *
 * Wave 19 #111 (2026-02-09): added inline mic + 2-pane voice review per
 * Voice Dictation UI Consistency Contract §13. Tap the mic →
 * Whisper transcribe → Claude refine → review modal → on commit, the
 * refined text replaces the field value AND the parent's `onSave` is
 * called immediately (we treat the voice commit as an explicit blur).
 */
export default function BlurTextarea({
  value,
  onSave,
  testid,
  placeholder = "",
  className = "hv-input mt-1 min-h-[80px]",
  disabled = false,
  rows,
  voice = true,                    // voice opt-in (default on)
  voiceContext = "lead_activity",  // refine context — see voice.py::_CTX
  voiceTestIdPrefix,
}) {
  const [draft, setDraft] = useState(value || "");
  const focusedRef = useRef(false);
  useEffect(() => {
    if (!focusedRef.current) {
      setDraft(value || "");
    }
  }, [value]);

  // ── Voice ──────────────────────────────────────────────────────────
  const cap = useVoiceCapture({ context: voiceContext });
  const [reviewOpen, setReviewOpen] = useState(false);
  const [raw, setRaw] = useState("");
  const [refined, setRefined] = useState("");
  const [committing, setCommitting] = useState(false);
  const committedAtRef = useRef(0);
  useEffect(() => {
    if (!cap.result) return;
    // Ignore stale `cap.result` if we've just committed — prevents the
    // review modal from re-opening immediately after the user clicks
    // "Use refined" (the previous race-condition that looked like a
    // glitch and ate the saved text).
    if (Date.now() - committedAtRef.current < 1500) return;
    setRaw(cap.result.raw || "");
    setRefined(cap.result.refined || cap.result.raw || "");
    setReviewOpen(true);
  }, [cap.result]);

  const commitVoice = async (text) => {
    if (committing) return;
    setCommitting(true);
    committedAtRef.current = Date.now();
    // Update local draft + close the review modal BEFORE touching cap.result.
    setDraft(text);
    setReviewOpen(false);
    try {
      if (text !== (value || "")) {
        await Promise.resolve(onSave(text));
      }
    } catch (e) {
      // Surface the failure so the user knows the save didn't go through.
      // Voice flows previously failed silently which felt like a "glitch".
      // eslint-disable-next-line no-console
      console.error("[BlurTextarea] onSave failed:", e);
      try {
        const { toast } = await import("./ui/sonner");
        toast?.error?.(e?.response?.data?.detail || e?.message || "Could not save voice note.");
      } catch (_e) { /* noop */ }
    } finally {
      cap.setResult(null);
      setCommitting(false);
    }
  };

  const fmt = (s) => `${Math.floor((s || 0) / 60)}:${String((s || 0) % 60).padStart(2, "0")}`;
  const tip = voiceTestIdPrefix || (testid ? `${testid}-voice` : "blur-voice");

  return (
    <>
      <div className="relative">
        <textarea
          value={draft}
          disabled={disabled || cap.recording || cap.uploading}
          rows={rows}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => { focusedRef.current = true; }}
          onBlur={() => {
            focusedRef.current = false;
            if (draft !== (value || "")) onSave(draft);
          }}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.currentTarget.blur();
            }
          }}
          className={`${className} ${voice ? "pr-10" : ""}`}
          placeholder={placeholder}
          data-testid={testid}
        />
        {voice && !disabled && (
          <div className="absolute bottom-1.5 right-1.5 flex items-center gap-1 print:hidden">
            {cap.recording && (
              <span
                className="text-[10px] font-mono text-rose-300 px-1.5 py-0.5 rounded bg-rose-500/10 border border-rose-500/30"
                data-testid={`${tip}-elapsed`}
              >
                {fmt(cap.elapsedSec)}
              </span>
            )}
            {cap.uploading ? (
              <button
                type="button"
                disabled
                className="w-7 h-7 rounded-full flex items-center justify-center bg-[#1FB6C1]/15 border border-[#1FB6C1]/40 text-[#1FB6C1]"
                title="Transcribing & refining…"
                data-testid={`${tip}-uploading`}
              >
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              </button>
            ) : cap.recording ? (
              <button
                type="button"
                onClick={cap.stop}
                className="w-7 h-7 rounded-full flex items-center justify-center bg-rose-500/20 border border-rose-500/50 text-rose-300 hover:bg-rose-500/30 transition-colors"
                title="Stop recording"
                data-testid={`${tip}-stop`}
                style={{
                  boxShadow: cap.level > 0
                    ? `0 0 ${4 + cap.level * 14}px rgba(244,63,94,${0.3 + cap.level * 0.4})`
                    : "none",
                }}
              >
                <MicOff className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={cap.start}
                className="w-7 h-7 rounded-full flex items-center justify-center bg-white/5 border border-white/15 text-[#a0aabf] hover:bg-[#A78BFA]/15 hover:border-[#A78BFA]/40 hover:text-[#C4B5FD] transition-colors"
                title="Dictate this field — Hidro will transcribe and clean up"
                data-testid={`${tip}-start`}
              >
                <Mic className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
      {cap.error && (
        <div className="mt-1 text-[11px] text-rose-300" data-testid={`${tip}-error`}>{cap.error}</div>
      )}

      {reviewOpen && (
        <Modal
          open
          onClose={() => { setReviewOpen(false); cap.setResult(null); }}
          title="Voice note review"
          testId={`${tip}-review-modal`}
          maxWidth="max-w-7xl"
          footer={
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => { setReviewOpen(false); cap.setResult(null); }}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid={`${tip}-review-cancel`}
              >
                <XIcon className="w-3 h-3" /> Discard
              </button>
              <div className="flex items-center gap-2 ml-auto">
                <button
                  type="button"
                  onClick={() => commitVoice(raw.trim())}
                  className="hv-btn hv-btn-ghost text-xs"
                  data-testid={`${tip}-review-use-raw`}
                >Use raw</button>
                <button
                  type="button"
                  onClick={() => commitVoice(refined.trim())}
                  disabled={!refined.trim() || committing}
                  className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
                  data-testid={`${tip}-review-use-refined`}
                >
                  {committing ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                  {committing ? "Saving…" : "Use refined"}
                </button>
              </div>
            </div>
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border border-white/10 rounded-md overflow-hidden bg-black/20 flex flex-col">
              <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#647087] border-b border-white/10 flex items-center gap-1.5 shrink-0">
                <Mic className="w-3 h-3" /> Raw transcript
                <span className="ml-auto text-[10px] normal-case tracking-normal text-[#475063]">edit if a name was mis-heard</span>
              </div>
              <textarea
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                rows={22}
                className="hv-input w-full text-sm leading-relaxed border-0 rounded-none bg-transparent focus:ring-0 flex-1 resize-y min-h-[300px] md:min-h-[60vh] font-normal"
                data-testid={`${tip}-review-raw-text`}
              />
            </div>
            <div className="border border-[#A78BFA]/30 rounded-md overflow-hidden bg-[#A78BFA]/[0.04] flex flex-col">
              <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#C4B5FD] border-b border-[#A78BFA]/30 flex items-center gap-1.5 shrink-0">
                <Sparkles className="w-3 h-3" /> Hidro-refined
              </div>
              <textarea
                value={refined}
                onChange={(e) => setRefined(e.target.value)}
                rows={22}
                className="hv-input w-full text-sm leading-relaxed border-0 rounded-none bg-transparent focus:ring-0 flex-1 resize-y min-h-[300px] md:min-h-[60vh] font-normal"
                data-testid={`${tip}-review-refined-text`}
              />
            </div>
          </div>
          <div className="mt-2 text-[11px] text-[#647087]">
            Both versions are saved — the refined text fills the field, the raw transcript stays available so nothing is lost.
            {cap.result?.duration_sec ? ` · Recorded ${fmt(cap.result.duration_sec)}` : ""}
          </div>
        </Modal>
      )}
    </>
  );
}
